/**
 * Track event to Google Analytics dataLayer
 * @param {string} eventName - Name of the event
 * @param {Object} eventData - Additional event data (optional)
 */
const trackEvent = (eventName, eventData = {}) => {
  try {
    window.dataLayer = window.dataLayer || [];

    if (!eventName) {
      console.warn('trackEvent: Event name is required');
      return;
    }

    const data = {
      event: eventName,
      timestamp: new Date().toISOString(),
      page_url: window.location.href,
      ...eventData
    };

    window.dataLayer.push(data);
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};

/**
 * Derive location context from the closest Shopify section or semantic element
 * @param {HTMLElement} element - The clicked element
 * @returns {string} - Location identifier
 */
const getLocationContext = (element) => {
  // Try to find closest element with data-section-id (common in this theme)
  const sectionWithDataAttr = element.closest('[data-section-id]');
  if (sectionWithDataAttr) {
    const sectionId = sectionWithDataAttr.getAttribute('data-section-id');
    // Extract section type from patterns like "sections--19923630489767__footer" -> "footer"
    const match = sectionId.match(/__([^_]+)$/);
    if (match) {
      return match[1];
    }
    return sectionId;
  }

  // Fallback: Try to find closest Shopify section by id
  const section = element.closest('[id^="shopify-section-"]');
  if (section) {
    const sectionId = section.id.replace('shopify-section-', '');
    const cleanId = sectionId.replace(/^template--\d+__/, '');
    if (!cleanId || /^[0-9a-f]+$/i.test(cleanId)) {
      return 'section';
    }
    return cleanId;
  }

  // Fallback: check for common semantic landmarks
  const header = element.closest('header');
  if (header) return 'header';

  const footer = element.closest('footer');
  if (footer) return 'footer';

  const main = element.closest('main');
  if (main) return 'main_content';

  return 'page';
};

/**
 * Initialize global click tracking for tel: and mailto: links
 */
const initLinkTracking = () => {
  document.addEventListener('click', (event) => {
    // Find the closest anchor element (handles clicks on child elements within links)
    const link = event.target.closest('a[href]');
    if (!link) return;

    const href = link.href;
    if (!href) return;

    const location = getLocationContext(link);

    if (href.startsWith('tel:')) {
      const phoneNumber = href.replace('tel:', '');
      trackEvent('phone_click', {
        phone_number: phoneNumber,
        location: location
      });
    } else if (href.startsWith('mailto:')) {
      // Extract just the email, removing any query params
      const email = href.replace('mailto:', '').split('?')[0];
      trackEvent('email_click', {
        email: email,
        location: location
      });
    }
  });
};

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLinkTracking);
} else {
  initLinkTracking();
}

export default () => ({
  trackEvent
});